import { useState } from "react";
import { useLocation } from "wouter";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPasswordPage() {
  // Remove the token from the address bar as soon as this screen renders.
  const [token] = useState(() => {
    let stored = "";
    try {
      stored = window.sessionStorage.getItem("tpt_password_reset_token") || "";
      window.sessionStorage.removeItem("tpt_password_reset_token");
    } catch {
      // Browser storage may be disabled; the fragment remains available.
    }
    const value = stored
      || new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token")
      || new URLSearchParams(window.location.search).get("token")
      || "";
    window.history.replaceState(window.history.state, "", "/reset-password");
    return value;
  });
  const [, navigate] = useLocation();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token || submitting) return;
    if (password.length < 8) {
      setError("Use at least 8 characters for your new password.");
      return;
    }
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/api/auth/password-reset/confirm", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.message || "The reset link could not be used. Request a new one.");
      setPassword("");
      setConfirmation("");
      setComplete(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The reset link could not be used. Request a new one.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-lg">T</span>
          </div>
          <span className="text-2xl font-bold text-foreground">The Proper Tool</span>
        </div>
        <Card>
          <CardContent className="p-8">
            <h1 className="text-2xl font-bold mb-2">Reset your password</h1>
            {complete ? (
              <div role="status" className="space-y-5">
                <p className="text-sm text-muted-foreground">Your password has been updated. You can now sign in with your email and new password.</p>
                <Button className="w-full" onClick={() => navigate("/login")}>Go to sign in</Button>
              </div>
            ) : !token ? (
              <div className="space-y-5">
                <p role="alert" className="text-sm text-muted-foreground">This reset link is missing or invalid. Request a new link from the sign-in page.</p>
                <Button className="w-full" onClick={() => navigate("/login")}>Go to sign in</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <p className="text-sm text-muted-foreground">Choose a new password of at least 8 characters. This link works once and expires after 30 minutes.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="reset-new-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="reset-new-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      data-testid="input-reset-new-password"
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPassword(value => !value)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reset-confirm-password">Confirm new password</Label>
                  <Input
                    id="reset-confirm-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirmation}
                    onChange={event => setConfirmation(event.target.value)}
                    data-testid="input-reset-confirm-password"
                  />
                </div>
                {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full" disabled={submitting} data-testid="button-reset-password">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}